import type { OutgoingMessage } from '~/lib/models'
import { BroadcastComposerOutgoingMessageFetchResults } from '~/types/campaigns'
import { OutgoingMessageInterface } from '~/types/messages'

export async function getMemberCountForCampaign(id) {
  if (id) {
    const { memberCount } = await (
      await fetch('/api/campaigns/' + id + '/memberCount')
    ).json()
    return memberCount
  }
  return null
}

export async function getCannedResponseForCampaign(id, campaignId) {
  try {
    if (id && campaignId) {
      const response = await fetch(
        '/api/campaigns/' + campaignId + '/canned-responses/' + id
      )
      const { cannedResponse, message } = await response.json()
      if (response.status === 200) {
        return { cannedResponse, message: null }
      } else {
        return { cannedResponse: null, message: message }
      }
    }
    return { cannedResponse: null, message: 'INVALID' }
  } catch (e) {
    console.error(e)
    return { cannedResponse: null, message: 'ERROR' }
  }
}

export async function getFlowsForCampaign(id) {
  if (id) {
    const { flows } = await (
      await fetch('/api/campaigns/' + id + '/flows')
    ).json()
    return flows
  }
  return null
}

const NO_OUTGOING_MESSAGE: BroadcastComposerOutgoingMessageFetchResults = {
  outgoingMessage: undefined,
  isAbOutgoingMessage: undefined,
  childFlows: undefined,
  recurrenceRule: undefined
}

// Shown when the response carries no message of its own — a 403 answers with
// plain text, and a proxy or gateway can return HTML.
const UNREADABLE_FAILURE =
  'Something went wrong loading this broadcast. Please refresh and try again.'

export async function getOutgoingMessageForBlast(
  id,
  decodedMessageId,
  groups,
  editScheduled,
  isOrganizer = false
): Promise<BroadcastComposerOutgoingMessageFetchResults> {
  if (!id || !decodedMessageId) return NO_OUTGOING_MESSAGE

  const response = await fetch(
    `/api/campaigns/${id}/outgoing-messages/${decodedMessageId}${editScheduled ? '?editScheduled=true' : ''}`
  )
  // Not every failure answers in JSON, and an unparseable body must not reject:
  // the caller stores this result to leave its loading state, so a throw here
  // strands the page on its spinner.
  const body = await response.json().catch(() => null)

  // Every failure exit from this route answers with an organizer-worded
  // `error`, whatever the status. Passing it through is what lets the composer
  // say "this broadcast has already started sending" instead of its own
  // generic "already sent — compose a new message", which is the opposite of
  // what an organizer should do while a send is in flight.
  if (!response.ok) {
    return { ...NO_OUTGOING_MESSAGE, error: body?.error ?? UNREADABLE_FAILURE }
  }

  const { outgoingMessage, isAbOutgoingMessage, childFlows, recurrenceRule } =
    body ?? {}

  return {
    outgoingMessage: manipulateGroupsForBlastPage(
      outgoingMessage,
      groups,
      isOrganizer
    ),
    isAbOutgoingMessage,
    childFlows,
    recurrenceRule
  }
}

export function provideReadableJsonAttachment(attachment) {
  let fullAttachment
  try {
    fullAttachment = JSON.parse(attachment)
  } catch (e) {
    fullAttachment = attachment
  }
  return fullAttachment
}

function manipulateGroupsForBlastPage(
  outgoingMessage: OutgoingMessage,
  groups = [],
  isOrganizer = false
): OutgoingMessageInterface {
  if (!outgoingMessage) return null
  const groupOptions = groups
    .map(({ id, title }) => ({
      label: title,
      value: id as string | number
    }))
    .concat([{ label: 'All', value: 'all' }])

  const newOM = { ...outgoingMessage }

  newOM.recipients = newOM.recipients || {}

  // moving this to client side handling instead of from route.
  // only set if recipients is set.
  if (newOM.recipients.campaignGroups) {
    newOM.recipients.campaignGroups = checkAndFilterRecipientsGroups(
      newOM.recipients.campaignGroups,
      groupOptions,
      isOrganizer
    )
  }
  if (newOM.recipients.campaignGroups?.length === 0) {
    newOM.recipients.campaignGroups = [{ label: 'All', value: 'all' }]
  }
  if (newOM.recipients.excludeGroups) {
    newOM.recipients.excludeGroups = checkAndFilterRecipientsGroups(
      newOM.recipients.excludeGroups,
      groupOptions,
      isOrganizer
    )
  }

  newOM.attachment = newOM.attachment
    ? provideReadableJsonAttachment(newOM.attachment)
    : null

  return newOM
}

function checkAndFilterRecipientsGroups(
  groups,
  groupOptions,
  isOrganizer = false
) {
  if (isOrganizer) {
    // isOrganizer, assume unknown group is a Data Permission issue
    return (groups ?? []).map((g) => {
      const groupOption = groupOptions.find(
        (groupOption) => groupOption.value === g
      )
      return (
        groupOption ?? {
          value: g,
          label: 'Specified Group Not Found'
        }
      )
    })
  }
  // isAdmin, filter out unknown group
  return groupOptions.filter((g) => (groups ?? []).includes(g.value))
}

/** Custom Fields */
export const addCustomField = async ({
  fieldName,
  campaignId,
  customType,
  options,
  existingKey
}) => {
  const newField = {
    name: fieldName,
    type: customType,
    options,
    identifier: existingKey
  }
  const response = await fetch(`/api/campaigns/${campaignId}/custom-fields`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(newField)
  })
  const result = await response.json()
  return result
}

export const updateCustomFieldOptions = async ({
  field,
  options,
  campaignId
}) => {
  return addCustomField({
    fieldName: field.label || field.value,
    campaignId,
    customType: field.type,
    options,
    existingKey: field.value
  })
}

export const deleteCustomField = async (campaignId, existingKey) => {
  const newField = {
    identifier: existingKey
  }
  const response = await fetch(`/api/campaigns/${campaignId}/custom-fields`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(newField)
  })
  const result = await response.json()
  return result
}
